import React from 'react';

// evaluateAST is a safe evaluator for the schema logic
const evaluateAST = (subjectMarks, classCohort, schemas) => {
  const schema = schemas?.find(s => s.cohort === classCohort);
  let total = 0;
  if (schema && schema.code) {
    try {
      const safeCode = (schema.code || '0').replace(/window|document|localStorage|sessionStorage|fetch|xmlhttprequest|eval/gi, '');
      const calculate = new Function('marks', `
        try {
          const result = ${safeCode};
          return typeof result === 'number' ? result : Number(result) || 0;
        } catch(e) {
          return 0;
        }
      `);
      total = calculate(subjectMarks);
    } catch (err) {
      console.error('Schema eval error:', err);
      total = 0;
    }
  } else {
    if (['Class VI', 'Class VII', 'Class VIII'].includes(classCohort)) {
      const t1 = ((subjectMarks.pt1 || 0) / 2) + ((subjectMarks.pt2 || 0) / 2) + ((subjectMarks.half_yearly || 0) / 2) + (subjectMarks.multiple_assessment1 || 0) + (subjectMarks.portfolio1 || 0) + (subjectMarks.enrichment1 || 0);
      const t2 = ((subjectMarks.pt3 || 0) / 2) + ((subjectMarks.pt4 || 0) / 2) + ((subjectMarks.annual || 0) / 2) + (subjectMarks.multiple_assessment2 || 0) + (subjectMarks.portfolio2 || 0) + (subjectMarks.enrichment2 || 0);
      total = (t1 * 0.4) + (t2 * 0.6);
    } else {
      const bestTwo = [subjectMarks.pre_mid || 0, subjectMarks.mid || 0, subjectMarks.post_mid || 0].sort((a,b)=>b-a);
      const avg = (bestTwo[0] + bestTwo[1]) / 2;
      total = (avg * 0.1) + (subjectMarks.multiple_assessment || 0) + (subjectMarks.portfolio || 0) + (subjectMarks.enrichment || 0) + (subjectMarks.annual || 0);
    }
  }
  return { total: total.toFixed(1), grade: getGrade(total, classCohort) };
};

const getGrade = (score, classCohort) => {
  if (classCohort === 'Class IX' || classCohort === 'Class X') {
    if (score >= 91) return 'A1';
    if (score >= 81) return 'A2';
    if (score >= 71) return 'B1';
    if (score >= 61) return 'B2';
    if (score >= 51) return 'C1';
    if (score >= 41) return 'C2';
    if (score >= 33) return 'D';
    if (score >= 21) return 'E1';
    return 'E2';
  } else {
    if (score >= 91) return 'A1';
    if (score >= 81) return 'A2';
    if (score >= 71) return 'B1';
    if (score >= 61) return 'B2';
    if (score >= 51) return 'C1';
    if (score >= 41) return 'C2';
    if (score >= 33) return 'D';
    return 'E';
  }
};

const CO_SCHOLASTIC_AREAS = [
  { id: 'work_education', label: 'Work Education' },
  { id: 'art_education', label: 'Art and Craft' },
  { id: 'health_pe', label: 'Health and Physical Education' },
  { id: 'computer', label: 'Computer' },
];

function formatDOB(dob) {
  if (!dob) return '—';
  const d = new Date(dob);
  if (!isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
  return dob;
}

export const ClassReportTemplate = React.forwardRef(({ student, marks, schemas, academicYear, targetClass, section, schoolName, coScholasticGrades, attendance, pageBreakBefore, rank }, ref) => {
  if (!student) return null;

  const getCoScholasticGrade = (areaId, termId) => {
    if (coScholasticGrades) {
      const entry = coScholasticGrades.find(
        g => g.studentId === student.id && g.area === areaId && g.term === termId
      );
      if (entry) return entry.grade;
    }
    return '';
  };

  const getAttendance = (term, field) => {
    if (attendance) {
      const entry = attendance.find(a => a.studentId === student.id && a.term === term);
      if (entry) return entry[field];
    }
    return '';
  };

  const subjects = ['English', 'Hindi', 'Bengali', 'Mathematics', 'Science', 'Social Science'];
  let grandTotalSum = 0;

  return (
    <div ref={ref} className="bg-white text-black p-4 mx-auto box-border" style={{ width: '210mm', minHeight: '297mm', pageBreakBefore: pageBreakBefore ? 'always' : 'auto' }}>
      <div className="border-[3px] border-black p-4 h-full flex flex-col font-sans">
        
        {/* Header with Logos */}
        <div className="flex items-center justify-between border-b-[3px] border-black pb-4 mb-4">
          <div className="w-24 flex items-center justify-center">
            <img src="/CBSE Logo.png" alt="CBSE" className="w-20 h-auto object-contain" onError={(e) => { e.target.src = '/logo-circle.png'; }} />
          </div>
          <div className="flex-1 text-center px-2">
            <h1 className="text-2xl font-black uppercase tracking-tight leading-none mb-2" style={{ fontFamily: 'Times New Roman, serif' }}>RESIDENTIAL SCHOOL, BHURATALI</h1>
            <h2 className="text-sm font-bold uppercase leading-none mb-1">BHURATALI :: SABROOM :: SouthTripura</h2>
            <p className="text-[10px] leading-tight mb-1">Under<br/>Tripura Tribal Welfare Residential Educational Institutions Society (TTWREIS)</p>
            <p className="text-[10px] font-semibold leading-tight">(Affiliated to CBSE, New Delhi, Affl. No.: 2020008, School Code: 35415, UDISE CODE: 16021002414)</p>
            <h3 className="text-lg font-bold mt-3 uppercase tracking-widest">PROGRESS REPORT CARD</h3>
            <p className="text-xs font-semibold mt-1">Academic Session: {academicYear}</p>
          </div>
          <div className="w-24 flex items-center justify-center">
            <img src="/School Logo.jpeg" alt="School" className="w-24 h-auto object-contain" onError={(e) => { e.target.src = '/logo-circle.png'; }} />
          </div>
        </div>

        {/* Student Details */}
        <div className="grid grid-cols-[3fr_2fr] gap-x-8 gap-y-1 text-sm font-semibold mb-4 px-2">
          <div className="space-y-1">
            <p className="flex"><span className="w-48 inline-block">Roll No.</span> <span className="mr-2">:</span> <span>{student.rollNo}</span></p>
            <p className="flex"><span className="w-48 inline-block">Student's Name</span> <span className="mr-2">:</span> <span>{student.name}</span></p>
            <p className="flex"><span className="w-48 inline-block">Father's Name</span> <span className="mr-2">:</span> <span>{student.guardianName || '—'}</span></p>
            <p className="flex"><span className="w-48 inline-block">Mother's/Guardian's Name</span> <span className="mr-2">:</span> <span>{student.motherName || '—'}</span></p>
            <p className="flex"><span className="w-48 inline-block">Date of Birth</span> <span className="mr-2">:</span> <span>{formatDOB(student.dob)}</span></p>
            <p className="flex"><span className="w-48 inline-block">Class & Section</span> <span className="mr-2">:</span> <span>{targetClass} '{section}'</span></p>
          </div>
          <div className="space-y-1">
            <p className="flex"><span className="w-24 inline-block">PEN No.</span> <span className="mr-2">:</span> <span>{student.penNo || ''}</span></p>
          </div>
        </div>

        {/* Marks Table */}
        <table className="w-full border-collapse border border-black text-[9px] text-center mb-4 font-semibold" style={{ tableLayout: 'fixed' }}>
          {['Class VI', 'Class VII', 'Class VIII'].includes(targetClass) ? (
            <>
              <thead>
                <tr>
                  <th className="border border-black p-1 text-left w-20" rowSpan={2}>Subject</th>
                  <th className="border border-black p-1" colSpan={6}>Term – I (100 Marks)</th>
                  <th className="border border-black p-1" colSpan={6}>Term – II (100 Marks)</th>
                  <th className="border border-black p-1 w-9" rowSpan={2}>40% from Term - I</th>
                  <th className="border border-black p-1 w-9" rowSpan={2}>60% from Term - II</th>
                  <th className="border border-black p-1 w-10" rowSpan={2}>Grand Total</th>
                  <th className="border border-black p-1 w-8" rowSpan={2}>Grade</th>
                </tr>
                <tr>
                  <th className="border border-black p-1 w-8">Periodic Test - I<br/><br/>(20)</th>
                  <th className="border border-black p-1 w-8">Periodic Test - II<br/><br/>(20)</th>
                  <th className="border border-black p-1 w-8">Half Yearly Exam<br/><br/>(40)</th>
                  <th className="border border-black p-1 w-8" style={{ fontSize: '8px' }}>Multiple Assessment<br/><br/>(10)</th>
                  <th className="border border-black p-1 w-8">Portfolio<br/><br/>(5)</th>
                  <th className="border border-black p-1 w-8" style={{ fontSize: '8px' }}>Subject Enrichment<br/><br/>(5)</th>

                  <th className="border border-black p-1 w-8">Periodic Test - III<br/><br/>(20)</th>
                  <th className="border border-black p-1 w-8">Periodic Test - IV<br/><br/>(20)</th>
                  <th className="border border-black p-1 w-8">Annual Exam<br/><br/>(40)</th>
                  <th className="border border-black p-1 w-8" style={{ fontSize: '8px' }}>Multiple Assessment<br/><br/>(10)</th>
                  <th className="border border-black p-1 w-8">Portfolio<br/><br/>(5)</th>
                  <th className="border border-black p-1 w-8" style={{ fontSize: '8px' }}>Subject Enrichment<br/><br/>(5)</th>
                </tr>
              </thead>
              <tbody>
                {subjects.map(sub => {
                  const m = marks.filter(mk => mk.studentId === student.id && mk.subject === sub);
                  const getM = (cid) => m.find(x => x.componentId === cid)?.score || 0;

                  const pt1 = getM('pt1') / 2;
                  const pt2 = getM('pt2') / 2;
                  const halfYearly = getM('half_yearly') / 2;
                  const ma1 = getM('multiple_assessment1');
                  const p1 = getM('portfolio1');
                  const se1 = getM('enrichment1');
                  const t1 = pt1 + pt2 + halfYearly + ma1 + p1 + se1;
                  const fortyPercent = t1 * 0.4;

                  const pt3 = getM('pt3') / 2;
                  const pt4 = getM('pt4') / 2;
                  const annual = getM('annual') / 2;
                  const ma2 = getM('multiple_assessment2');
                  const p2 = getM('portfolio2');
                  const se2 = getM('enrichment2');
                  const t2 = pt3 + pt4 + annual + ma2 + p2 + se2;
                  const sixtyPercent = t2 * 0.6;

                  const overall = evaluateAST({
                    pt1: getM('pt1'), pt2: getM('pt2'), half_yearly: getM('half_yearly'), 
                    multiple_assessment1: ma1, portfolio1: p1, enrichment1: se1,
                    pt3: getM('pt3'), pt4: getM('pt4'), annual: getM('annual'), 
                    multiple_assessment2: ma2, portfolio2: p2, enrichment2: se2
                  }, targetClass, schemas);

                  grandTotalSum += Number(overall.total) || 0;

                  return (
                    <tr key={sub}>
                      <td className="border border-black p-1 text-left">{sub}</td>
                      <td className="border border-black p-1">{pt1 || ''}</td>
                      <td className="border border-black p-1">{pt2 || ''}</td>
                      <td className="border border-black p-1">{halfYearly || ''}</td>
                      <td className="border border-black p-1">{ma1 || ''}</td>
                      <td className="border border-black p-1">{p1 || ''}</td>
                      <td className="border border-black p-1">{se1 || ''}</td>
                      <td className="border border-black p-1">{pt3 || ''}</td>
                      <td className="border border-black p-1">{pt4 || ''}</td>
                      <td className="border border-black p-1">{annual || ''}</td>
                      <td className="border border-black p-1">{ma2 || ''}</td>
                      <td className="border border-black p-1">{p2 || ''}</td>
                      <td className="border border-black p-1">{se2 || ''}</td>
                      <td className="border border-black p-1">{fortyPercent.toFixed(1) || ''}</td>
                      <td className="border border-black p-1">{sixtyPercent.toFixed(1) || ''}</td>
                      <td className="border border-black p-1">{overall.total || ''}</td>
                      <td className="border border-black p-1">{overall.grade || ''}</td>
                    </tr>
                  );
                })}
                <tr className="bg-gray-100">
                  <td className="border border-black p-1 text-right pr-4" colSpan={15}>Grand Total (500)</td>
                  <td className="border border-black p-1" colSpan={2}>{grandTotalSum.toFixed(1)}</td>
                </tr>
                <tr className="bg-gray-100">
                  <td className="border border-black p-1 text-center" colSpan={4}>Aggregate %</td>
                  <td className="border border-black p-1" colSpan={4}>{((grandTotalSum / 500) * 100).toFixed(1)}%</td>
                  <td className="border border-black p-1 text-center" colSpan={5}>Rank</td>
                  <td className="border border-black p-1" colSpan={4}>{rank || '—'}</td>
                </tr>
              </tbody>
            </>
          ) : (
            <>
              <thead>
                <tr>
                  <th className="border border-black p-1 text-left w-24" rowSpan={2}>Subject</th>
                  <th className="border border-black p-1" colSpan={4}>Internal Assessment (20)</th>
                  <th className="border border-black p-1 w-16" rowSpan={2}>Internal Ass. Total (20)</th>
                  <th className="border border-black p-1 w-16" rowSpan={2}>Annual Exam (80)</th>
                  <th className="border border-black p-1 w-16" rowSpan={2}>Grand Total (100)</th>
                  <th className="border border-black p-1 w-12" rowSpan={2}>Grade</th>
                </tr>
                <tr>
                  <th className="border border-black p-1 w-12">Pre-Mid<br/><br/>(50)</th>
                  <th className="border border-black p-1 w-12">Mid<br/><br/>(50)</th>
                  <th className="border border-black p-1 w-12">Post-Mid<br/><br/>(50)</th>
                  <th className="border border-black p-1 w-12">Avg Best 2<br/><br/>(5)</th>
                </tr>
              </thead>
              <tbody>
                {subjects.map(sub => {
                  const m = marks.filter(mk => mk.studentId === student.id && mk.subject === sub);
                  const getM = (cid) => m.find(x => x.componentId === cid)?.score || 0;

                  const preMid = getM('pre_mid');
                  const mid = getM('mid');
                  const postMid = getM('post_mid');
                  const bestTwo = [preMid, mid, postMid].sort((a,b)=>b-a);
                  const avgBest2 = ((bestTwo[0] + bestTwo[1])/2) * 0.1;

                  const internal = avgBest2 + getM('multiple_assessment') + getM('portfolio') + getM('enrichment');
                  
                  const overall = evaluateAST({
                    pre_mid: preMid, mid, post_mid: postMid,
                    multiple_assessment: getM('multiple_assessment'),
                    portfolio: getM('portfolio'),
                    enrichment: getM('enrichment'),
                    annual: getM('annual')
                  }, targetClass, schemas);

                  grandTotalSum += Number(overall.total) || 0;

                  return (
                    <tr key={sub}>
                      <td className="border border-black p-1 text-left">{sub}</td>
                      <td className="border border-black p-1">{preMid || ''}</td>
                      <td className="border border-black p-1">{mid || ''}</td>
                      <td className="border border-black p-1">{postMid || ''}</td>
                      <td className="border border-black p-1">{avgBest2 || ''}</td>
                      <td className="border border-black p-1">{internal || ''}</td>
                      <td className="border border-black p-1">{getM('annual') || ''}</td>
                      <td className="border border-black p-1">{overall.total || ''}</td>
                      <td className="border border-black p-1">{overall.grade || ''}</td>
                    </tr>
                  );
                })}
                <tr className="bg-gray-100">
                  <td className="border border-black p-1 text-right pr-4" colSpan={7}>Grand Total (500)</td>
                  <td className="border border-black p-1" colSpan={2}>{grandTotalSum.toFixed(1)}</td>
                </tr>
                <tr className="bg-gray-100">
                  <td className="border border-black p-1 text-center" colSpan={3}>Aggregate %</td>
                  <td className="border border-black p-1" colSpan={2}>{((grandTotalSum / 500) * 100).toFixed(1)}%</td>
                  <td className="border border-black p-1 text-center" colSpan={2}>Rank</td>
                  <td className="border border-black p-1" colSpan={2}>{rank || '—'}</td>
                </tr>
              </tbody>
            </>
          )}
        </table>

        {/* Footer Grid: Co-Scholastic & Attendance */}
        <div className="grid grid-cols-2 gap-4 mb-8 text-[10px] font-semibold">
          <div>
            <table className="w-full border-collapse border border-black text-center">
              <thead>
                <tr>
                  <th className="border border-black p-1 text-left w-32">Co-scholastic Areas:<br/><span className="text-[8px] font-normal">[on a 3 point (A-C) grading scale]</span></th>
                  <th className="border border-black p-1">TERM - I</th>
                  <th className="border border-black p-1">TERM - II</th>
                </tr>
              </thead>
              <tbody>
                {CO_SCHOLASTIC_AREAS.map((area, index) => (
                  <tr key={area.id}>
                    <td className="border border-black p-1 text-left flex gap-2">
                       <span className="w-4 inline-block text-center">{index + 1}</span> {area.label}
                    </td>
                    <td className="border border-black p-1">{getCoScholasticGrade(area.id, 'Term 1')}</td>
                    <td className="border border-black p-1">{getCoScholasticGrade(area.id, 'Term 2')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <table className="w-full border-collapse border border-black text-center mt-6">
              <thead>
                <tr>
                  <th className="border border-black p-1" colSpan={3}>ATTENDANCE</th>
                </tr>
                <tr>
                  <th className="border border-black p-1 text-left w-32"></th>
                  <th className="border border-black p-1">TERM - I</th>
                  <th className="border border-black p-1">TERM - II</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-black p-1 text-left">No. of Working Days</td>
                  <td className="border border-black p-1">{getAttendance('Term 1', 'workingDays')}</td>
                  <td className="border border-black p-1">{getAttendance('Term 2', 'workingDays')}</td>
                </tr>
                <tr>
                  <td className="border border-black p-1 text-left">No. of Days Attended the School</td>
                  <td className="border border-black p-1">{getAttendance('Term 1', 'attendedDays')}</td>
                  <td className="border border-black p-1">{getAttendance('Term 2', 'attendedDays')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-auto">
          <p className="font-semibold text-sm mb-16 flex items-center gap-2">
            Result: 
            <span className="border-b border-black inline-block min-w-[16rem] px-2 text-center pb-0.5 whitespace-nowrap">
              {(() => {
                const status = (student.academicStatus || '').toUpperCase();
                const isPromoted = status === 'PROMOTED';
                const isFailed = status === 'FAILED';
                const maxTotal = subjects.length * 100;
                const isDistinction = ((grandTotalSum / maxTotal) * 100) >= 80;

                return (
                  <>
                    {isPromoted && <span className="text-green-600 font-bold tracking-wider">PROMOTED</span>}
                    {isFailed && <span className="text-red-600 font-bold tracking-wider">FAILED</span>}
                    {isDistinction && <span className="text-purple-600 font-bold tracking-wider ml-2">DISTINCTION</span>}
                  </>
                );
              })()}
            </span>
          </p>
          
          {/* Signatures */}
          <div className="flex justify-between items-end">
            <div className="flex gap-2">
              <span className="font-semibold text-sm">Date:</span>
              <span className="border-b border-black inline-block w-32 text-center text-sm">{new Date().toLocaleDateString('en-GB')}</span>
            </div>
            <div className="text-center flex flex-col items-center">
              <div className="w-48 border-b border-black"></div>
              <span className="font-bold text-sm mt-1">Signature of Class Teacher</span>
            </div>
            <div className="text-center flex flex-col items-center">
              <div className="w-48 border-b border-black"></div>
              <span className="font-bold text-sm mt-1">Signature of Principal</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
